import { useState } from "react";
import type { ReactElement } from "react";
import { TextFeld, WahlFeld, feldId } from "./felder.js";
import { mitWert, wertAn } from "../layoutEntwurf.js";
import type { PanelProps } from "../LayoutRegister.js";

/**
 * Register „Texte": die Titel der Belegart, die das Blatt gerade zeigt, und die
 * Rückfalltexte. Die Korrekturrechnung ERBT die Titel der Rechnung ausser dem Betreff
 * (`erbtVon` im Katalog, docs/sektionen.md): Ihr Platzhalter ist dann der Wert, den die
 * Rechnung im Entwurf hat — genau das, was die Engine drucken würde.
 */
/** Höchstlänge eines Rückfalltexts — die Grenze der Whitelist (Fixrunde 1, W1). */
const TEXT_MAX = 2000;

export function TextePanel(p: PanelProps): ReactElement {
  const k = p.stand.katalog;
  const art = k.belegarten.find((b) => b.art === p.belegart) ?? k.belegarten[0];
  const erbe = art === undefined || art.erbtVon === null ? null
    : k.belegarten.find((b) => b.art === art.erbtVon) ?? null;
  return (
    <div className="lay__panel">
      {art !== undefined && (
        <fieldset className="lay__gruppe">
          <legend>Titel für {art.label}</legend>
          {erbe !== null && (
            <p className="lay__panelhinweis">Erbt von {erbe.label} — ein leerer Titel
              übernimmt deren Wert, der Betreff bleibt eigen.</p>
          )}
          {art.titel.map((t) => {
            const pfad = ["belegarten", art.art, t.schluessel];
            const geerbt = erbe !== null && t.schluessel !== "betreff"
              ? wertAn(p.entwurf, ["belegarten", erbe.art, t.schluessel]) : undefined;
            return (
              <TextFeld key={t.schluessel} id={feldId(pfad.join("."))} label={t.label}
                wert={wertAn(p.entwurf, pfad)} mehrzeilig={false}
                systemwert={typeof geerbt === "string" ? geerbt : t.vorgabe}
                hinweis={typeof geerbt === "string" && erbe !== null
                  ? `geerbt von ${erbe.label}` : undefined}
                fehler={p.feldFehler[pfad.join(".")]}
                onWert={(neu) => p.onEntwurf(mitWert(p.entwurf, pfad, neu))}
                onVerlassen={p.onFeldVerlassen} />
            );
          })}
        </fieldset>
      )}
      <NummerierungGruppe {...p} />
      <fieldset className="lay__gruppe">
        <legend>Rückfalltexte</legend>
        <p className="lay__panelhinweis">Gelten, wenn der Beleg keinen eigenen Text trägt.
          Die Fußzeile ist einzeilig.</p>
        {k.texte.map((t) => (
          <TextFeld key={t.id} id={feldId(`texte.${t.id}`)} label={t.label}
            wert={wertAn(p.entwurf, ["texte", t.id])} systemwert={t.wert}
            mehrzeilig={t.mehrzeilig} fehler={p.feldFehler[`texte.${t.id}`]}
            max={TEXT_MAX} markdown
            onWert={(neu) => p.onEntwurf(mitWert(p.entwurf, ["texte", t.id], neu))}
            onVerlassen={p.onFeldVerlassen} />
        ))}
      </fieldset>
    </div>
  );
}

/**
 * ÜBERSCHRIFTEN-NUMMERIERUNG (Migration 048, Dirk 249/254): das Schema dieses Layouts für Einleitung, Ausleitung und
 * Anlagen. Die Wahl schreibt der Editor sofort (eigener Weg mit Bestätigung), wirksam wird sie mit der Freigabe;
 * das Beispiel zeigt die feste Folge H1 H2 H3 H3 H4 H5 H6 H2 H1. Rückfalltexte setzt die Engine selbst — sie
 * bleiben ohne Nummern.
 */
function NummerierungGruppe(p: PanelProps): ReactElement {
  const gewaehlt = p.stand.nummerierung.entwurfId;
  const schema = gewaehlt === null ? null : p.nummerierungen?.find((n) => n.id === gewaehlt) ?? null;
  const frei = p.stand.nummerierung.freigegeben;
  const optionen = [{ wert: "", text: "Keine" },
    ...(p.nummerierungen ?? []).map((n) => ({ wert: n.id, text: n.name }))];
  // Nach jeder Wahl (geschrieben oder abgebrochen) neu gezeichnet: Die Auswahl zeigt dann wieder den Stand.
  const [runde, setRunde] = useState(0);
  return (
    <fieldset className="lay__gruppe">
      <legend>Nummerierung der Überschriften</legend>
      <p className="lay__panelhinweis">Gilt für Einleitung, Ausleitung und Anlagen ab der nächsten Freigabe.
        Schemata pflegt Verwaltung › Nummerierung; ein Textblock weicht mit „nummerierung: neu“, „aus“ oder einem
        Schemanamen im Front-Matter ab.</p>
      <WahlFeld key={runde} id={feldId("nummerierung")} label="Schema" wert={gewaehlt ?? ""} optionen={optionen}
        fehler={undefined}
        onWahl={(w) => { void p.onNummerierung(w === "" ? null : w).finally(() => setRunde((r) => r + 1)); }} />
      {schema !== null && (
        <p className="lay__panelhinweis">
          Beispiel: {schema.beispiel.map((b) => (b.nummer === "" ? "–" : b.nummer)).join(" · ")}
        </p>
      )}
      {(frei?.id ?? null) !== gewaehlt && (
        <p className="lay__panelhinweis">Freigegeben ist: {frei === null ? "Keine" : frei.name}.</p>
      )}
    </fieldset>
  );
}
